import { useState } from 'react';
import {
  Box, Button, Chip, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import {
  dateTimeLocalAISO,
  isoAFechaDateTimeLocal,
} from '../../../utils/horaLocal';

const labelTipo = (t) => {
  if (t.tipo === 'MOVIMIENTO') {
    return t.subtipo === 'ENTRADA' ? '📦 Compra' : t.subtipo === 'VENTA' ? '🛍️ Venta' : '📤 Salida';
  }
  if (t.tipo === 'GASTO') return '💸 Gasto';
  if (t.tipo === 'TRASPASO') return '🔄 Traspaso';
  if (t.subtipo === 'INYECCIÓN') return '🔄 Inyección';
  if (t.subtipo === 'RETIRO') return '🔄 Retiro';
  return t.subtipo === 'SOBRANTE' ? '⬆️ Sobrante' : '⬇️ Faltante';
};

const colorTipo = (t) => {
  if (t.anulado) return 'default';
  if (t.tipo === 'MOVIMIENTO') return t.subtipo === 'ENTRADA' ? 'success' : 'primary';
  if (t.tipo === 'GASTO') return 'warning';
  if (t.tipo === 'TRASPASO') return 'secondary';
  if (t.subtipo === 'INYECCIÓN') return 'secondary';
  if (t.subtipo === 'RETIRO') return 'error';
  return t.subtipo === 'SOBRANTE' ? 'success' : 'error';
};

export default function TablaTransacciones({
  transacciones,
  fechaFormato,
  transaccionEnEdicion,
  valoresEdicion,
  onIniciarEdicion,
  onCancelarEdicion,
  onGuardarEdicion,
  onCambioMovimiento,
  cambiarValorEdicion,
  onAnular,
  onEditarFecha,
  onConvertirGastoALimpieza,
  onCorregir,
  onHistorial,
  diaBloqueado = false,
}) {
  const [editandoFecha, setEditandoFecha] = useState(null);
  const [nuevaFecha, setNuevaFecha] = useState('');

  const iniciarEdicionFecha = (t) => {
    setEditandoFecha(t.id);
    setNuevaFecha(isoAFechaDateTimeLocal(t.fechaISO));
  };

  const guardarFecha = (t) => {
    if (nuevaFecha) {
      const fechaISO = dateTimeLocalAISO(nuevaFecha);
      onEditarFecha(t, fechaISO);
    }
    setEditandoFecha(null);
    setNuevaFecha('');
  };

  const cancelarEdicionFecha = () => {
    setEditandoFecha(null);
    setNuevaFecha('');
  };

  if (transacciones.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Transacciones del Día — {fechaFormato}
        </Typography>
        <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
          ℹ️ Sin transacciones registradas para este día
        </Typography>
      </Paper>
    );
  }

  const estiloAnulado = {
    textDecoration: 'line-through',
    opacity: 0.45,
  };

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Transacciones del Día — {fechaFormato}
      </Typography>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell><strong>Hora</strong></TableCell>
              <TableCell><strong>Tipo</strong></TableCell>
              <TableCell><strong>Descripción</strong></TableCell>
              <TableCell align="right"><strong>Cantidad/Monto</strong></TableCell>
              <TableCell align="right"><strong>Precio Unit.</strong></TableCell>
              <TableCell align="right"><strong>Total</strong></TableCell>
              <TableCell><strong>Método</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transacciones.map((t) => {
              const enEdicion = transaccionEnEdicion === t.id;
              const anulado = t.anulado;
              const estiloCelda = anulado ? estiloAnulado : {};

              return (
                <TableRow
                  key={t.id}
                  sx={{
                    '&:hover': { bgcolor: anulado ? '#fafafa' : '#f5f5f5' },
                    bgcolor: anulado ? '#f9f9f9' : enEdicion ? '#e3f2fd' : 'inherit',
                  }}
                >
                  {/* HORA */}
                  <TableCell sx={estiloCelda}>
                    {editandoFecha === t.id ? (
                      <TextField
                        size="small" type="datetime-local"
                        value={nuevaFecha}
                        onChange={(e) => setNuevaFecha(e.target.value)}
                        sx={{ width: 200 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      new Date(t.fechaISO).toLocaleTimeString('es-ES', {
                        hour: '2-digit', minute: '2-digit', second: '2-digit'
                      })
                    )}
                  </TableCell>

                  {/* TIPO */}
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip label={labelTipo(t)} size="small" color={colorTipo(t)}
                        sx={anulado ? { textDecoration: 'line-through', opacity: 0.5 } : {}} />
                      {anulado && <Chip label="ANULADO" size="small" color="error" variant="outlined" sx={{ fontSize: '0.65rem' }} />}
                      {t.esGasto && t.anulado && t.objOriginal?.convertidoEnLimpiezaId && (
                        <Chip label="Convertido a limpieza" size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                      )}
                      {t.objOriginal?.convertidoATipo && <Chip label={'Convertido a ' + t.objOriginal.convertidoATipo} size="small" color="info" variant="outlined" />}
                      {t.objOriginal?.pendingSync && (
                        <Chip label="Pendiente sync" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                      )}
                    </Stack>
                  </TableCell>

                  {/* DESCRIPCIÓN */}
                  <TableCell sx={estiloCelda}>{t.display}</TableCell>

                  {/* CANTIDAD / MONTO */}
                  <TableCell align="right" sx={estiloCelda}>
                    {enEdicion && t.esMovimiento ? (
                      <TextField
                        size="small" type="number"
                        value={valoresEdicion.cantidad}
                        onChange={(e) => onCambioMovimiento('cantidad', e.target.value)}
                        inputProps={{ step: '0.01' }} sx={{ width: 100 }}
                      />
                    ) : enEdicion && (t.esGasto || t.esAjuste || t.esTraspaso) ? (
                      <TextField
                        size="small" type="number"
                        value={valoresEdicion.monto}
                        onChange={(e) => cambiarValorEdicion('monto', e.target.value)}
                        inputProps={{ step: '0.01' }} sx={{ width: 100 }}
                      />
                    ) : (
                      <strong>{t.esMovimiento ? `${t.cantidad}u` : `${t.monto?.toFixed(2)}`}</strong>
                    )}
                  </TableCell>

                  {/* PRECIO UNITARIO */}
                  <TableCell align="right" sx={estiloCelda}>
                    {enEdicion && t.esMovimiento ? (
                      <TextField
                        size="small" type="number"
                        value={valoresEdicion.precio}
                        onChange={(e) => onCambioMovimiento('precio', e.target.value)}
                        inputProps={{ step: '0.01' }} sx={{ width: 100 }}
                      />
                    ) : (
                      <>{t.esMovimiento ? `${t.precio?.toFixed(2)}` : '—'}</>
                    )}
                  </TableCell>

                  {/* TOTAL */}
                  <TableCell align="right" sx={estiloCelda}>
                    {enEdicion && t.esMovimiento ? (
                      <TextField
                        size="small" type="number"
                        value={valoresEdicion.total}
                        onChange={(e) => onCambioMovimiento('total', e.target.value)}
                        inputProps={{ step: '0.01' }} sx={{ width: 100 }}
                      />
                    ) : (
                      <>{t.esMovimiento ? `${(t.cantidad * t.precio).toFixed(2)}` : '—'}</>
                    )}
                  </TableCell>

                  {/* MÉTODO PAGO */}
                  <TableCell sx={estiloCelda}>{t.metodoPago || '—'}</TableCell>

                  {/* ACCIONES */}
                  <TableCell align="center">
                    {anulado ? (
                      t.objOriginal?.reemplazadoPorCorreccionId && onHistorial ? <Button size="small" onClick={() => onHistorial(t.objOriginal.reemplazadoPorCorreccionId)}>Ver cambio</Button> : <Typography variant="caption" color="text.disabled">—</Typography>
                    ) : editandoFecha === t.id ? (
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Button size="small" variant="contained" color="success" onClick={() => guardarFecha(t)}>✓</Button>
                        <Button size="small" variant="contained" color="error" onClick={cancelarEdicionFecha}>✕</Button>
                      </Stack>
                    ) : enEdicion ? (
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Button size="small" variant="contained" color="success" onClick={() => onGuardarEdicion(t)}>✓</Button>
                        <Button size="small" variant="contained" color="error" onClick={onCancelarEdicion}>✕</Button>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {onCorregir && t.puedeEditar && <>
                          <Button size="small" variant="outlined" disabled={diaBloqueado || t.objOriginal?.pendingSync} onClick={() => onCorregir(t, 'EDITAR')}>Editar</Button>
                          <Button size="small" variant="outlined" disabled={diaBloqueado || t.objOriginal?.pendingSync} onClick={() => onCorregir(t)}>Convertir</Button>
                          <Button size="small" color="error" disabled={diaBloqueado || t.objOriginal?.pendingSync} onClick={() => onCorregir(t, 'ANULAR')}>Anular</Button>
                        </>}
                        {onCorregir && (t.objOriginal?.origenOperacion === 'LIMPIEZA' || t.objOriginal?.operacionId) && <Button size="small" disabled={diaBloqueado} onClick={() => onCorregir(t)}>Corregir limpieza completa</Button>}
                        {t.objOriginal?.arqueoDiaId && <Typography variant="caption">Corregir desde el arqueo</Typography>}
                        {!onCorregir && t.puedeEditar && (
                          <>
                            <Button size="small" variant="outlined" onClick={() => onIniciarEdicion(t)} title="Editar">✎</Button>
                            <Button size="small" variant="outlined" color="info" onClick={() => iniciarEdicionFecha(t)} title="Cambiar fecha">🕐</Button>
                            <Button size="small" variant="outlined" color="error" onClick={() => onAnular(t)} title="Anular">✕</Button>
                            {t.esGasto && onConvertirGastoALimpieza && !t.objOriginal?.convertidoEnLimpiezaId && (
                              <Button
                                size="small"
                                variant="outlined"
                                // TEMP: Metalplast migración gastos→limpieza
                                onClick={() => onConvertirGastoALimpieza(t.objOriginal)}
                              >
                                Convertir en limpieza
                              </Button>
                            )}
                          </>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}
